import React from "react";
import { Icon, PlainButton } from "@culturehq/components";
import { errorService, Trans } from "app/utils";
import { AI_PROMPT_TYPE, CHILD_PROMPT_TYPE, OLD_PROMPT_TYPE } from "app/utils/constants";
import { makePatch, makePost } from "@culturehq/client";
import QuickOptions from "components/display/QuickOptions";
import ChqModalConfirmDelete from "components/common/ChqModalConfirmDelete";
import StoryQuestionModal from "./story-questions/StoryQuestionModal";
import NewStoryQuestionModal from "./story-questions/NewStoryQuestionModal";
import NestStoryQuestionModal from "./story-questions/NestStoryQuestionModal";
import DuplicateStoryQuestionOption from "./story-questions/DuplicateStoryQuestionOption";
import NewAIStoryQuestionModal from "./story-questions/NewAIStoryQuestionModal";
import SubPromptModal from "./story-questions/SubPromptModal";

const StoryQuestionModalHandler = ({ children, storyQuestion, onUpdate }) => {
  const handleUpdate = async updatedStoryQuestion => {
    const processedStoryQuestion = {
      ...storyQuestion,
      ...updatedStoryQuestion,
      storyQuestionType: storyQuestion.storyQuestionType === "old_prompt" || storyQuestion.storyQuestionType === "child_prompt"
        ? "child_prompt"
        : storyQuestion.storyQuestionType,
      subprompts: updatedStoryQuestion.subprompts?.map(subprompt => ({
        ...subprompt,
        instructions: subprompt.instructions || subprompt.summary
      })) || storyQuestion.childStoryQuestions?.map(subprompt => ({
        ...subprompt,
        instructions: subprompt.instructions || subprompt.summary
      }))
    };

    const body = {
      question: processedStoryQuestion.question,
      summary: processedStoryQuestion.summary,
      allowedTypes: processedStoryQuestion.allowedTypes,
      imageUrl: processedStoryQuestion.imageUrl,
      status: processedStoryQuestion.status || "active",
      visibility: processedStoryQuestion.visibility || "private_access",
      points: processedStoryQuestion.points,
      mappersValueIds: processedStoryQuestion.mappersValueIds,
      tags: processedStoryQuestion.tags || []
    };

    try {
      const response = await makePatch(`/story_questions/${storyQuestion.id}`, body);
      onUpdate(response.storyQuestion);
    } catch (error) {
      errorService.error(error);
    }
  };

  if (storyQuestion.storyQuestionType === AI_PROMPT_TYPE) {
    return (
      <NewAIStoryQuestionModal storyQuestion={storyQuestion} onUpdate={onUpdate}>
        {children}
      </NewAIStoryQuestionModal>
    );
  }

  if (storyQuestion.parentStoryQuestion) {
    return (
      <SubPromptModal storyQuestion={storyQuestion} onUpdate={handleUpdate}>
        {children}
      </SubPromptModal>
    );
  }

  return (
    <StoryQuestionModal
      storyQuestion={storyQuestion}
      onUpdate={onUpdate}
      selectedOptions={storyQuestion.storyQuestionTags}
    >
      {children}
    </StoryQuestionModal>
  );
};

const OldAndChildStoryQuestionQuickOptions = ({
  storyQuestion,
  onCreate,
  onUpdate,
  onReplace,
  onDelete,
  onAccept
}) => (
  <QuickOptions>
    <ul className="dropdown-menu">
      {(storyQuestion.storyQuestionType === OLD_PROMPT_TYPE
        || storyQuestion.storyQuestionType === CHILD_PROMPT_TYPE) && (
        <>
          <li>
            <NewStoryQuestionModal
              storyQuestion={storyQuestion}
              onUpdate={onUpdate}
              onReplace={onReplace}
              selectedOptions={storyQuestion.storyQuestionTags}
            >
              {onTrigger => (
                <PlainButton onClick={onTrigger}>
                  <Icon icon="target" />
                  <Trans>Migrate to new Story Trend</Trans>
                </PlainButton>
              )}
            </NewStoryQuestionModal>
          </li>
          <li>
            <NestStoryQuestionModal
              storyQuestion={storyQuestion}
              onUpdate={onUpdate}
              onDelete={onDelete}
            >
              {onTrigger => (
                <PlainButton onClick={onTrigger}>
                  <Icon className="Subprompt-Arrow" icon="nested-element" />
                  <Trans>Nest inside an existing trend</Trans>
                </PlainButton>
              )}
            </NestStoryQuestionModal>
          </li>
        </>
      )}
      <li>
        <StoryQuestionModalHandler storyQuestion={storyQuestion} onUpdate={onUpdate}>
          {onTrigger => (
            <PlainButton onClick={onTrigger}>
              <Icon icon="edit-outlined" />
              <Trans>Edit</Trans>
            </PlainButton>
          )}
        </StoryQuestionModalHandler>
      </li>
      {storyQuestion.storyQuestionType !== AI_PROMPT_TYPE && (
        <li>
          <DuplicateStoryQuestionOption type="prompt" storyQuestion={storyQuestion} onCreate={onCreate} />
        </li>
      )}
      <li>
        <ChqModalConfirmDelete
          onAccept={() => onAccept(storyQuestion)}
          trigger={onTrigger => (
            <PlainButton onClick={onTrigger}>
              <Icon icon="trash-a-outlined" />
              <Trans>Delete</Trans>
            </PlainButton>
          )}
        >
          <p><Trans>Are you sure you would like to delete this story prompt?</Trans></p>
          <p>
            <Trans>
              All the stories that belong to this story prompt will be deleted too
            </Trans>
          </p>
        </ChqModalConfirmDelete>
      </li>
    </ul>
  </QuickOptions>
);

export default OldAndChildStoryQuestionQuickOptions;
